import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";

export interface PlanMetadataSettings {
    planMetadataUrl: string;
    seksjonUrl: string;
}

export interface PlanMetadata {
    objectId?: number;
    planId: string;
    kommunenummer?: number;
    publisert: boolean;
    publisertDato: Date | null;
    sistRedigertDato: Date | null;
    seksjonCount: number;
    antallVisninger: number;
    PlanensBeskrivelse?: string;
    Planregisterlink?: string;
    GjeldendeBestemmelser?: string;
}

export class PlanMetadataService {
    private metadataLayer: FeatureLayer | null = null;
    private seksjonLayer: FeatureLayer | null = null;
    private settings: PlanMetadataSettings | null = null;
    private rowIds = new Map<string, number>();
    private rowFetching = new Map<string, Promise<number>>();

    configure(settings: PlanMetadataSettings) {
        this.settings = settings;
        this.metadataLayer = null;
        this.seksjonLayer = null;
        this.rowIds.clear();
        this.rowFetching.clear();
    }

    private getMetadataLayer(): FeatureLayer {
        if (!this.settings?.planMetadataUrl) throw new Error('PlanMetadataService: planMetadataUrl not configured');
        this.metadataLayer ??= new FeatureLayer({ url: this.settings.planMetadataUrl });
        return this.metadataLayer;
    }

    private getSeksjonLayer(): FeatureLayer {
        if (!this.settings?.seksjonUrl) throw new Error('PlanMetadataService: seksjonUrl not configured');
        this.seksjonLayer ??= new FeatureLayer({ url: this.settings.seksjonUrl });
        return this.seksjonLayer;
    }

    async fetchMetadata(): Promise<Map<string, PlanMetadata>> {
        const [metaRows, countMap] = await Promise.all([
            this.settings?.planMetadataUrl ? this.fetchMetadataRows() : Promise.resolve([]),
            this.settings?.seksjonUrl ? this.fetchSeksjonCounts() : Promise.resolve(new Map<string, number>()),
        ]);

        const result = new Map<string, PlanMetadata>();

        for (const row of metaRows) {
            result.set(row.planId, {
                ...row,
                seksjonCount: countMap.get(row.planId) ?? 0,
            });
        }

        // Plans with sections but no metadata row yet → "utkast"
        for (const [planId, count] of countMap) {
            if (!result.has(planId)) {
                result.set(planId, {
                    planId,
                    publisert: false,
                    publisertDato: null,
                    sistRedigertDato: null,
                    seksjonCount: count,
                    antallVisninger: 0,
                });
            }
        }

        return result;
    }

    private async fetchMetadataRows(): Promise<Omit<PlanMetadata, 'seksjonCount'>[]> {
        const query = this.getMetadataLayer().createQuery();
        query.where = '1=1';
        query.outFields = ['*'];

        const result = await this.getMetadataLayer().queryFeatures(query);
        return result.features.map(f => {
            const a = f.attributes ?? {};
            return {
                objectId: a.OBJECTID as number | undefined,
                planId: String(a.PlanId ?? ''),
                kommunenummer: typeof a.Kommunenummer === 'number' ? a.Kommunenummer : undefined,
                publisert: Boolean(a.Publisert),
                publisertDato: typeof a.PublisertDato === 'number' ? new Date(a.PublisertDato) : null,
                sistRedigertDato: typeof a.SistRedigertDato === 'number' ? new Date(a.SistRedigertDato) : null,
                antallVisninger: typeof a.AntallVisninger === 'number' ? a.AntallVisninger : 0,
                PlanensBeskrivelse: typeof a.PlanensBeskrivelse === 'string' ? a.PlanensBeskrivelse : undefined,
                Planregisterlink: typeof a.Planregisterlink === 'string' ? a.Planregisterlink : undefined,
                GjeldendeBestemmelser: typeof a.GjeldendeBestemmelser === 'string' ? a.GjeldendeBestemmelser : undefined,
            };
        });
    }

    async fetchSeksjonCounts(): Promise<Map<string, number>> {
        const query = this.getSeksjonLayer().createQuery();
        query.where = "PlanId IS NOT NULL AND PlanId <> ''";
        query.outStatistics = [{
            statisticType: 'count',
            onStatisticField: 'OBJECTID',
            outStatisticFieldName: 'Antall',
        } as __esri.StatisticDefinition];
        (query as unknown as Record<string, unknown>).groupByFieldsForStatistics = ['PlanId'];

        const result = await this.getSeksjonLayer().queryFeatures(query);
        const countMap = new Map<string, number>();
        for (const f of result.features) {
            const planId = String(f.attributes?.PlanId ?? '');
            const count = Number(f.attributes?.Antall ?? 0);
            if (planId) countMap.set(planId, count);
        }
        return countMap;
    }

    private getOrCreateRow(planId: string, kommunenummer?: number): Promise<number> {
        if (this.rowIds.has(planId)) return Promise.resolve(this.rowIds.get(planId)!);
        if (this.rowFetching.has(planId)) return this.rowFetching.get(planId)!;

        const promise = this._fetchOrCreate(planId, kommunenummer)
            .then(id => { this.rowIds.set(planId, id); return id; })
            .finally(() => { this.rowFetching.delete(planId); });
        this.rowFetching.set(planId, promise);
        return promise;
    }

    private async _fetchOrCreate(planId: string, kommunenummer?: number): Promise<number> {
        const sanitized = planId.replace(/'/g, "''");
        const query = this.getMetadataLayer().createQuery();
        query.where = `PlanId = '${sanitized}'`;
        query.outFields = ['OBJECTID'];

        const existing = await this.getMetadataLayer().queryFeatures(query);
        if (existing.features.length > 0) {
            return existing.features[0].attributes.OBJECTID as number;
        }

        const feature = new Graphic({
            attributes: { PlanId: planId, ...(kommunenummer != null ? { Kommunenummer: kommunenummer } : {}) },
        });
        const result = await this.getMetadataLayer().applyEdits({ addFeatures: [feature] });
        if (result.addFeatureResults?.[0]?.error) {
            throw new Error(result.addFeatureResults[0].error.message ?? 'Failed to create metadata row');
        }
        return result.addFeatureResults[0].objectId as number;
    }

    // Registrerer at planen er redigert. Endrer BEVISST ikke Publisert:
    // synlighet styres kun av Publisert = 1, og en redigering skal ikke
    // avpublisere en publisert plan (jf. bug 13735 — tidligere satte denne
    // Publisert=0, som i det stille tok publiserte planer «ut av admin-status»
    // mens de forble synlige for publikum via den gamle PublisertDato-grenen).
    // Avpublisering skjer kun eksplisitt via unpublishPlan.
    async registrerRedigering(planId: string, kommunenummer?: number): Promise<void> {
        const now = Date.now();
        const objectId = await this.getOrCreateRow(planId, kommunenummer);
        await this.getMetadataLayer().applyEdits({
            updateFeatures: [new Graphic({ attributes: { OBJECTID: objectId, SistRedigertDato: now } })],
        });
    }

    get isMetadataConfigured(): boolean {
        return !!this.settings?.planMetadataUrl;
    }

    async updateBeskrivelse(planId: string, beskrivelse: string, kommunenummer?: number): Promise<void> {
        const objectId = await this.getOrCreateRow(planId, kommunenummer);
        await this.getMetadataLayer().applyEdits({
            updateFeatures: [new Graphic({ attributes: { OBJECTID: objectId, PlanensBeskrivelse: beskrivelse } })],
        });
    }

    async updatePlanregisterlink(planId: string, url: string, kommunenummer?: number): Promise<void> {
        const objectId = await this.getOrCreateRow(planId, kommunenummer);
        await this.getMetadataLayer().applyEdits({
            updateFeatures: [new Graphic({ attributes: { OBJECTID: objectId, Planregisterlink: url } })],
        });
    }

    async updateGjeldendeBestemmelser(planId: string, url: string, kommunenummer?: number): Promise<void> {
        const objectId = await this.getOrCreateRow(planId, kommunenummer);
        await this.getMetadataLayer().applyEdits({
            updateFeatures: [new Graphic({ attributes: { OBJECTID: objectId, GjeldendeBestemmelser: url } })],
        });
    }

    async publishPlan(planId: string, kommunenummer?: number): Promise<void> {
        if (!this.isMetadataConfigured) return;

        const now = Date.now();
        const objectId = await this.getOrCreateRow(planId, kommunenummer);
        const feature = new Graphic({ attributes: { OBJECTID: objectId, Publisert: 1, PublisertDato: now } });
        const result = await this.getMetadataLayer().applyEdits({ updateFeatures: [feature] });
        const updated = result.updateFeatureResults?.[0];
        if (updated?.error) throw new Error(updated.error.message ?? 'Failed to publish plan');
    }

    async unpublishPlan(planId: string): Promise<void> {
        if (!this.isMetadataConfigured) return;

        const sanitized = planId.replace(/'/g, "''");
        const query = this.getMetadataLayer().createQuery();
        query.where = `PlanId = '${sanitized}'`;
        query.outFields = ['OBJECTID'];

        const existing = await this.getMetadataLayer().queryFeatures(query);
        if (existing.features.length === 0) return;

        const objectId = existing.features[0].attributes.OBJECTID as number;
        const feature = new Graphic({
            attributes: { OBJECTID: objectId, Publisert: 0, PublisertDato: null },
        });
        const result = await this.getMetadataLayer().applyEdits({ updateFeatures: [feature] });
        const updated = result.updateFeatureResults?.[0];
        if (updated?.error) throw new Error(updated.error.message ?? 'Failed to unpublish plan');
    }
}
